import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import FastMarquee from 'react-fast-marquee';
import { createPortal } from 'react-dom';

import { useClassName, useResize, useWindowSize } from 'common/hooks';
import { ControlledTabs } from 'public/components/Tabs';
import { SVGIcon } from 'common/components';
import { Modal } from 'common/lazy';
import { STANDARD_EVENTS } from 'utils/analytics/trackV2';

import useSlider from '../../../Home/Components/useSlider';
import CarouselArrows from '../CarouselArrows';
import CarouselControls from '../CarouselControls';
import CarouselCounter from '../CarouselCounter';
import CarouselTypeE from './CarouselTypeE';
import CarouselTypeF from './CarouselTypeF';
import CarouselAsset from './CarouselAsset';
import CarouselCard from './CarouselCard';

import useCustomStyle from '../../hooks';
import useLoop from '../../../Home/Components/useLoop';

import './carousel.less';
import 'swiper/swiper.less';

function Tabs({ setTagIndex, tagIndex, tags, handleTrack }) {
  const classNames = useClassName('CLPCarousel');

  if (!tags?.length) return null;

  return (
    <ControlledTabs
      names={tags}
      selectedIndex={tagIndex}
      styles={{ container: classNames('tabs') }}
      onClick={(tagName) => {
        setTagIndex(tags.findIndex((tag) => tag === tagName));
      }}
      handleTrack={handleTrack}
    />
  );
}

function getBreakpoints(component) {
  const slidesPerView = component?.fields?.settings?.find(
    (setting) => setting.fields.key === '--slides-per-view'
  );
  if (!slidesPerView)
    return {
      0: {
        slidesPerView: 'auto',
      },
    };

  return {
    0: {
      slidesPerView: Number(slidesPerView.fields.mobileValue),
    },
    767: {
      slidesPerView: Number(slidesPerView.fields.tabletValue),
    },
    1128: {
      slidesPerView: Number(slidesPerView.fields.desktopValue),
    },
  };
}

function Slider({ component, tagIndex, paused, setPaused, handleTrack }) {
  const classNames = useClassName('CLPCarousel');
  const [, styles] = useCustomStyle(component.fields.settings);
  const [props, methods, state] = useSlider();
  const breakpoints = getBreakpoints(component);
  const tags = [
    ...new Set(component.fields.slides.map((a) => a.fields.tags).flat()),
  ].filter(Boolean);
  const slides = component.fields.groupTags
    ? component.fields.slides.filter((slide) =>
        slide.fields?.tags?.includes(tags[tagIndex])
      )
    : component.fields.slides;
  const loop = useLoop({
    breakpoints,
    slidesLength: slides.length,
    showPreview: styles['--show-preview'] !== 'false',
  });
  const transitionSpeed = parseInt(styles['--transition-speed']) || 300;
  const hiddenCounter =
    styles['--carousel-hidden-counter'] === 'true' ? true : false;
  const hiddenOverflow = Object.keys(breakpoints).length > 1;
  const assetLinksEnabled = styles['--asset-links-enabled'] === 'true';

  return (
    <>
      <Swiper
        height={styles['--height'] || 500}
        centeredSlides={styles['--centered-slides'] !== 'false'}
        slidesPerView="auto"
        spaceBetween={parseInt(styles['--space-between']) || 0}
        speed={transitionSpeed}
        {...props}
        {...loop}
        className={classNames([
          'wrapper',
          !loop.loop && 'no-loop',
          hiddenOverflow && 'hidden-overflow',
        ])}
        style={Object.fromEntries(
          Object.entries(styles)
            .filter(([key]) => key.startsWith('--swiper'))
            .map(([key, value]) => [key.replace('--swiper-', ''), value])
        )}>
        {slides.map((slide, index) => (
          <SwiperSlide
            key={index}
            className={classNames([
              'slide-container',
              slides.length === 1 && 'single',
            ])}
            style={Object.fromEntries(
              Object.entries(styles)
                .filter(([key]) => key.startsWith('--slide'))
                .map(([key, value]) => [key.replace('--slide-', ''), value])
            )}>
            {({ isActive }) => {
              return slide.sys.contentType.sys.id === 'lpAsset' ? (
                <CarouselAsset
                  linkEnabled={assetLinksEnabled}
                  slide={slide}
                  setPaused={setPaused}
                  settings={component.fields.settings}
                  isActive={isActive}
                />
              ) : (
                <CarouselCard
                  slide={slide}
                  setPaused={setPaused}
                  isActive={isActive}
                  handleTrack={handleTrack}
                />
              );
            }}
          </SwiperSlide>
        ))}
      </Swiper>
      {slides.length > 1 && loop.loop && (
        <CarouselControls settings={component.fields.settings}>
          {!hiddenCounter && (
            <CarouselCounter
              current={state.activeIndex + 1}
              total={slides.length}
              next={methods.slideNext}
              goTo={methods.slideTo}
              paused={paused}
              autoplayDuration={component.fields.autoplayDuration}
              type={component.fields.type}
              theme={component.fields.theme}
            />
          )}
          <CarouselArrows
            next={methods.slideNext}
            prev={methods.slidePrev}
            type={component.fields.type}
            theme={component.fields.theme}
            settings={component.fields.settings}
            trackDirection={(direction) =>
              handleTrack &&
              handleTrack({
                event: STANDARD_EVENTS.carouselSlide,
                params: {
                  carousel_name: component?.fields?.name,
                  direction,
                },
              })
            }
          />
        </CarouselControls>
      )}
    </>
  );
}

// Manual fill: duplicate slides until we have enough to cover wide viewports
// without depending on react-fast-marquee's autoFill (which causes a
// ResizeObserver loop in dense layouts).
const MARQUEE_MIN_SLIDES = 20;

function Marquee({ component, onClick, ...props }) {
  const [, styles] = useCustomStyle(component.fields.settings);
  const slides = component.fields.slides;
  const repeatCount = Math.max(
    1,
    Math.ceil(MARQUEE_MIN_SLIDES / Math.max(slides.length, 1))
  );
  const filledSlides = Array(repeatCount).fill(slides).flat();

  return (
    <FastMarquee pauseOnHover {...props}>
      {filledSlides.map((slide, idx) => {
        return (
          <div
            key={`${slide.sys.id}-${idx}`}
            style={{
              margin: '8px',
              borderRadius: '32px',
              overflow: 'hidden',
              cursor: 'pointer',
              ...Object.fromEntries(
                Object.entries(styles)
                  .filter(([key]) => key.startsWith('--slide'))
                  .map(([key, value]) => [key.replace('--slide-', ''), value])
              ),
            }}
            onClick={() => onClick(slide.sys.id)}>
            {slide.sys.contentType.sys.id === 'lpAsset' ? (
              <CarouselAsset
                slide={slide}
                settings={component.fields.settings}
              />
            ) : (
              <CarouselCard slide={slide} />
            )}
          </div>
        );
      })}
    </FastMarquee>
  );
}

function TypeDCarousel({ component }) {
  const classNames = useClassName('CLPCarousel');
  const [setRef] = useCustomStyle(component.fields.settings);
  const { width: viewportWidth } = useWindowSize();
  const [containerNode, setContainerNode] = useState(null);
  const { width: containerWidth } = useResize(containerNode);
  const isReady = Boolean(viewportWidth && containerWidth);

  return (
    <div
      ref={(node) => {
        setContainerNode(node);
        setRef(node);
      }}
      className={classNames('type-d')}
      style={{ position: 'relative' }}>
      {isReady && (
        <Marquee
          key={`${viewportWidth}-${containerWidth}`}
          component={component}
          onClick={() => {}}
        />
      )}
    </div>
  );
}

function CarouselTypeC({ component, paused, setPaused, handleTrack }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const classNames = useClassName('CLPCarousel');

  const next = () => {
    if (selectedIndex === component.fields.slides.length - 1) {
      setSelectedIndex(0);
    } else {
      setSelectedIndex((selectedIndex) => selectedIndex + 1);
    }
  };

  const prev = () => {
    if (selectedIndex === 0) {
      setSelectedIndex(component.fields.slides.length - 1);
    } else {
      setSelectedIndex((selectedIndex) => selectedIndex - 1);
    }
  };

  const onClick = (id) => {
    setSelectedIndex(
      component.fields.slides.findIndex((slide) => slide.sys.id === id)
    );
  };

  const selected = component.fields.slides[selectedIndex];
  return (
    <>
      <Marquee
        onClick={onClick}
        component={{
          ...component,
          fields: {
            ...component.fields,
            slides: component.fields.slides.slice(
              0,
              Math.ceil(component.fields.slides.length / 2)
            ),
          },
        }}
      />
      <Marquee
        direction="right"
        onClick={onClick}
        component={{
          ...component,
          fields: {
            ...component.fields,
            slides: component.fields.slides.slice(
              Math.ceil(component.fields.slides.length / 2)
            ),
          },
        }}
      />
      {selected && (
        <Modal
          onClose={() => setSelectedIndex(null)}
          className={classNames('marquee-modal-wrapper')}
          centered
          open={true}>
          {createPortal(
            <div
              className={classNames('close-button')}
              onClick={() => setSelectedIndex(null)}>
              <SVGIcon name="close" size="tiny" />
            </div>,
            document.getElementsByTagName('body')[0]
          )}
          <div className={classNames('modal-container')}>
            <div className={classNames('marquee-modal')}>
              {selected.sys.contentType.sys.id === 'lpAsset' ? (
                <CarouselAsset
                  slide={selected}
                  paused={paused}
                  setPaused={setPaused}
                  settings={component.fields.settings}
                />
              ) : (
                <CarouselCard
                  slide={selected}
                  paused={paused}
                  setPaused={setPaused}
                  handleTrack={handleTrack}
                />
              )}
            </div>
            <CarouselControls>
              <CarouselCounter
                current={selectedIndex + 1}
                total={component.fields.slides.length}
                next={next}
                paused={paused}
                autoplayDuration={component.fields.autoplayDuration}
                type={'Type B'}
              />
              {createPortal(
                <CarouselArrows
                  next={next}
                  prev={prev}
                  type={'Type B'}
                  style={{ position: 'fixed', zIndex: 99999, display: 'flex' }}
                  trackDirection={(direction) =>
                    handleTrack && handleTrack(direction)
                  }
                />,
                document.getElementsByTagName('body')[0]
              )}
            </CarouselControls>
          </div>
        </Modal>
      )}
    </>
  );
}

export default function Carousel({ component, refs, handleTrack }) {
  const [paused, setPaused] = useState(false);
  const [tagIndex, setTagIndex] = useState(0);
  const [setRef] = useCustomStyle(component.fields.settings);

  if (component.fields.type === 'Type D') {
    return <TypeDCarousel component={component} />;
  }

  if (component.fields.type === 'Type C') {
    return (
      <div ref={setRef} style={{ position: 'relative' }}>
        <CarouselTypeC
          component={component}
          paused={paused}
          setPaused={setPaused}
          handleTrack={(direction) => {
            handleTrack &&
              handleTrack({
                event: STANDARD_EVENTS.carouselSlide,
                params: {
                  carousel_name: component?.fields?.name,
                  direction,
                },
              });
          }}
        />
      </div>
    );
  }

  if (component.fields.type === 'Type E') {
    return (
      <CarouselTypeE
        component={component}
        refs={refs}
        handleTrack={(direction) => {
          handleTrack &&
            handleTrack({
              event: STANDARD_EVENTS.carouselSlide,
              params: {
                carousel_name: component?.fields?.name,
                direction,
              },
            });
        }}
      />
    );
  }

  if (component.fields.type === 'Type F') {
    return (
      <CarouselTypeF
        component={component}
        refs={refs}
        handleTrack={(direction) => {
          handleTrack &&
            handleTrack({
              event: STANDARD_EVENTS.carouselSlide,
              params: {
                carousel_name: component?.fields?.name,
                direction,
              },
            });
        }}
      />
    );
  }

  const tags = [
    ...new Set(component.fields.slides.map((a) => a.fields.tags).flat()),
  ].filter(Boolean);

  const groupTags = Boolean(component.fields.groupTags && tags.length > 0);

  return (
    <div style={{ position: 'relative' }} ref={setRef}>
      {groupTags && (
        <Tabs
          tagIndex={tagIndex}
          setTagIndex={(i) => {
            setTagIndex(i);
          }}
          tags={tags}
          handleTrack={handleTrack}
        />
      )}

      {groupTags ? (
        tags.map((tag, index) => {
          return (
            <div
              key={index}
              {...(tagIndex !== index && { style: { display: 'none' } })}>
              <Slider
                component={component}
                tagIndex={index}
                paused={paused}
                setPaused={setPaused}
                handleTrack={handleTrack}
              />
            </div>
          );
        })
      ) : (
        <Slider
          component={component}
          paused={paused}
          setPaused={setPaused}
          handleTrack={handleTrack}
        />
      )}
    </div>
  );
}
